/**
 * user-backend billing APIs for Interview Copilot session time (PostgreSQL).
 * UPDATED: Now routes through Python FastAPI backend for session management
 */

const SAAS_BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const PYTHON_API_BASE_URL = (process.env.REACT_APP_PYTHON_API_BASE_URL || '').replace(/\/$/, '');
const AUTH_TOKEN_KEY = 'interview-copilot-auth-token';

export interface InterviewCopilotEntitlement {
  success?: boolean;
  isInternal?: boolean;
  showUsage?: boolean;
  canUse: boolean;
  unlimited?: boolean;
  usedSeconds?: number;
  limitSeconds?: number | null;
  remainingSeconds?: number | null;
  reason?: string;
  message?: string;
}

function getAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

function getUserId(): number | null {
  // Try to get user_id from auth-user-data JSON in localStorage
  try {
    const authUserData = localStorage.getItem('auth-user-data');
    if (authUserData) {
      const parsed = JSON.parse(authUserData);
      if (parsed.userId) {
        // Convert string to number if needed
        return typeof parsed.userId === 'number' 
          ? parsed.userId 
          : parseInt(parsed.userId, 10);
      }
    }
  } catch (e) {
    console.error('Error parsing auth-user-data:', e);
  }
  
  // Fallback: try direct user-id key
  const directUserId = localStorage.getItem('user-id');
  if (directUserId) {
    return parseInt(directUserId, 10);
  }
  
  return null;
}

async function billingFetch<T>(
  path: string,
  options: RequestInit = {},
  useExpressBackend: boolean = false
): Promise<T> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  const baseUrl = useExpressBackend ? SAAS_BACKEND_URL : PYTHON_API_BASE_URL;
  if (!baseUrl) {
    throw new Error('Backend URL is not configured');
  }

  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const err = new Error(data.error || data.message || `Request failed (${response.status})`);
    (err as Error & { code?: string; status?: number; details?: unknown }).code = data.code;
    (err as Error & { status?: number }).status = response.status;
    (err as Error & { details?: unknown }).details = data.details;
    throw err;
  }

  return data as T;
}

export async function getInterviewCopilotEntitlement(): Promise<InterviewCopilotEntitlement> {
  const userId = getUserId();
  if (!userId) {
    throw new Error('User ID not found');
  }
  
  return billingFetch<InterviewCopilotEntitlement>(
    `/api/billing/interview-copilot/entitlement?user_id=${userId}`,
    {},
    false // Use Python backend
  );
}

export async function startInterviewCopilotBillingSession(
  sessionId: string
): Promise<unknown> {
  const userId = getUserId();
  if (!userId) {
    throw new Error('User ID not found');
  }
  
  return billingFetch(`/api/billing/interview-copilot/sessions/start?user_id=${userId}`, {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  }, false); // Use Python backend
}

export async function stopInterviewCopilotBillingSession(
  sessionId: string
): Promise<unknown> {
  const userId = getUserId();
  if (!userId) {
    throw new Error('User ID not found');
  }
  
  return billingFetch(`/api/billing/interview-copilot/sessions/stop?user_id=${userId}`, {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  }, false); // Use Python backend
}

export function formatRemainingTime(remainingSeconds: number | null | undefined): string {
  if (remainingSeconds == null) return 'Unlimited';
  const hours = Math.floor(remainingSeconds / 3600);
  const minutes = Math.floor((remainingSeconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
}
